import { useEffect, useState, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Sparkles, Users, Mail, PhoneCall, BarChart3, Plus,
  Filter, Trash2, RefreshCw, Upload, ArrowRight, Search, Megaphone
} from "lucide-react";
import GrowthEngineWorkflows from "@/pages/GrowthEngineWorkflows";
import GrowthEngineCalling from "@/pages/GrowthEngineCalling";

/**
 * AdminGrowthEngine — Phase 1 UI.
 *
 * Three sub-tabs:
 *   • Prospects   → list, filter, create, promote-from-contact-form, status-change
 *   • Email Workflows → Phase 2 placeholder (n8n)
 *   • AI Calling     → Phase 3 placeholder (ElevenLabs + Twilio Voice)
 *
 * All colours + status pipeline metadata come from the backend
 * `/admin/growth/reference` endpoint so we don't have to duplicate the
 * status enum between Python and JSX.
 */
const SUB_TABS = [
  { key: "prospects", label: "Prospects", Icon: Users },
  { key: "email",     label: "Email Workflows", Icon: Mail },
  { key: "calling",   label: "AI Calling", Icon: PhoneCall },
  { key: "attribution", label: "Attribution", Icon: BarChart3 },
];

export default function AdminGrowthEngine() {
  const [sub, setSub] = useState("prospects");
  const [ref, setRef] = useState({ statuses: {}, regions: [], sources: [] });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/growth/reference");
        setRef(data);
      } catch (e) {
        toast.error("Failed to load reference data: " + (e.message || e));
      }
    })();
  }, []);

  return (
    <div className="space-y-5" data-testid="growth-engine">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600 flex items-center gap-1.5">
            <Sparkles size={12} /> AI Growth Engine
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mt-1">Prospect &amp; outreach pipeline</h2>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Central hub for SBT&apos;s own growth funnel — track prospects, run email workflows via n8n,
            dial with 11Labs AI voice agents, and roll everything up in one attribution view.
          </p>
        </div>
      </header>

      {/* Sub-tab strip */}
      <nav className="flex gap-1 border-b border-slate-200">
        {SUB_TABS.map(({ key, label, Icon }) => {
          const active = sub === key;
          return (
            <button
              key={key} onClick={() => setSub(key)}
              className={
                "px-4 py-2.5 text-sm font-semibold flex items-center gap-2 border-b-2 transition-colors " +
                (active
                  ? "border-amber-500 text-amber-700"
                  : "border-transparent text-slate-500 hover:text-slate-800")
              }
              data-testid={`ge-subtab-${key}`}
            >
              <Icon size={14} /> {label}
            </button>
          );
        })}
      </nav>

      {sub === "prospects"   && <ProspectsSub ref_data={ref} />}
      {sub === "email"       && <GrowthEngineWorkflows />}
      {sub === "calling"     && <GrowthEngineCalling />}
      {sub === "attribution" && <PhasePlaceholder title="Attribution & Cost Dashboard" phase="Phase 5" />}
    </div>
  );
}

// ────────────────────── PROSPECTS SUB-TAB ─────────────────────────────
function ProspectsSub({ ref_data }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ status: "", region: "", source: "", campaign_id: "", q: "" });
  const [selected, setSelected] = useState(null);   // full prospect object for detail drawer
  const [showNew, setShowNew] = useState(false);

  // Campaign layer state
  const [campaigns, setCampaigns] = useState([]);
  const [agents, setAgents] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [showCampaignMgr, setShowCampaignMgr] = useState(false);
  const [showAssign, setShowAssign] = useState(false);

  const loadCampaigns = useCallback(async () => {
    try {
      const [c, a, n] = await Promise.all([
        api.get("/admin/growth/campaigns"),
        api.get("/admin/growth/voice/agents"),
        api.get("/admin/growth/voice/numbers"),
      ]);
      setCampaigns(c.data.items || []);
      setAgents(a.data.items || []);
      setNumbers(n.data.items || []);
    } catch (e) { /* silent — campaign UI just hides */ }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.region) params.region = filters.region;
      if (filters.source) params.source = filters.source;
      if (filters.campaign_id) params.campaign_id = filters.campaign_id;
      if (filters.q) params.q = filters.q;
      const [{ data }, s] = await Promise.all([
        api.get("/admin/growth/prospects", { params }),
        api.get("/admin/growth/stats"),
      ]);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setStats(s.data);
    } catch (e) {
      toast.error("Load failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  // When source changes off "crm_app", clear the campaign filter.
  useEffect(() => {
    if (filters.source !== "crm_app" && filters.campaign_id) {
      setFilters((f) => ({ ...f, campaign_id: "" }));
    }
  }, [filters.source, filters.campaign_id]);

  const statusMeta = ref_data.statuses || {};
  const isCrmAppMode = filters.source === "crm_app";
  const activeCampaign = campaigns.find((c) => c.id === filters.campaign_id) || null;

  return (
    <div className="space-y-4" data-testid="ge-prospects">
      {/* Stat pills */}
      {stats && (
        <div className="flex flex-wrap gap-2">
          <StatPill label="Total" value={stats.total} tone="slate" />
          {Object.entries(stats.by_status || {}).slice(0, 8).map(([st, n]) => (
            <StatPill
              key={st}
              label={(statusMeta[st]?.emoji || "•") + " " + (statusMeta[st]?.label || st)}
              value={n}
              tone={statusMeta[st]?.color || "slate"}
              onClick={() => setFilters((f) => ({ ...f, status: st }))}
            />
          ))}
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 bg-white border border-slate-200 rounded-lg p-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={13} className="absolute left-3 top-2.5 text-slate-400"/>
          <input
            type="text" value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Search by name, email, or company…"
            className="w-full text-sm pl-8 pr-3 py-2 border border-slate-200 rounded focus:outline-none focus:border-amber-500"
            data-testid="ge-search"
          />
        </div>
        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                className="text-sm px-3 py-2 border border-slate-200 rounded" data-testid="ge-filter-status">
          <option value="">All statuses</option>
          {Object.entries(statusMeta).map(([k, m]) => <option key={k} value={k}>{m.emoji} {m.label}</option>)}
        </select>
        <select value={filters.region} onChange={(e) => setFilters({ ...filters, region: e.target.value })}
                className="text-sm px-3 py-2 border border-slate-200 rounded" data-testid="ge-filter-region">
          <option value="">All regions</option>
          {(ref_data.regions || []).map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
        </select>
        <select value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value, campaign_id: "" })}
                className="text-sm px-3 py-2 border border-slate-200 rounded" data-testid="ge-filter-source">
          <option value="">All sources</option>
          {(ref_data.sources || []).map((s) => (
            <option key={s} value={s}>{s === "crm_app" ? "CRM App" : s}</option>
          ))}
        </select>

        {/* Campaign dropdown appears only when source == crm_app */}
        {isCrmAppMode && (
          <>
            <select value={filters.campaign_id}
                    onChange={(e) => setFilters({ ...filters, campaign_id: e.target.value })}
                    className="text-sm px-3 py-2 border border-slate-200 rounded bg-amber-50/60"
                    data-testid="ge-filter-campaign">
              <option value="">All campaigns</option>
              {campaigns.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.lead_count || 0})</option>
              ))}
            </select>
            <button onClick={() => setShowCampaignMgr(true)}
                    className="text-xs px-3 py-1.5 border border-amber-300 bg-amber-50 text-amber-800 rounded hover:bg-amber-100 flex items-center gap-1 font-semibold"
                    data-testid="ge-manage-campaigns">
              <Megaphone size={12}/> Manage campaigns
            </button>
          </>
        )}

        {(filters.status || filters.region || filters.source || filters.campaign_id || filters.q) && (
          <button onClick={() => setFilters({ status: "", region: "", source: "", campaign_id: "", q: "" })}
                  className="text-xs text-slate-500 hover:text-red-600 px-2" data-testid="ge-clear-filters">Clear</button>
        )}
        <button onClick={load} className="text-xs px-3 py-1.5 border border-slate-200 rounded hover:bg-slate-50 flex items-center gap-1"
                data-testid="ge-refresh"><RefreshCw size={12}/> Refresh</button>
        <button onClick={() => setShowNew(true)}
                className="text-xs px-3 py-1.5 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 flex items-center gap-1"
                data-testid="ge-new-prospect"><Plus size={12}/> Add prospect</button>
      </div>

      {/* Campaign context bar — visible only when a specific campaign is picked */}
      {isCrmAppMode && activeCampaign && (
        <CampaignContextBar
          campaign={activeCampaign}
          leads={items}
          agents={agents}
          numbers={numbers}
          onChanged={() => { loadCampaigns(); load(); }}
          onAssignLeads={() => setShowAssign(true)}
        />
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr className="text-left">
                <th className="px-3 py-2 font-semibold">Name</th>
                <th className="px-3 py-2 font-semibold">Company</th>
                <th className="px-3 py-2 font-semibold">Region</th>
                <th className="px-3 py-2 font-semibold">Source</th>
                <th className="px-3 py-2 font-semibold">Status</th>
                <th className="px-3 py-2 font-semibold">Score</th>
                <th className="px-3 py-2 font-semibold">Updated</th>
                <th className="w-6"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && !loading && (
                <tr><td colSpan="8" className="p-8 text-center text-slate-400 text-sm">
                  No prospects yet — {filters.status || filters.region || filters.source || filters.campaign_id || filters.q ? "adjust filters or " : ""}
                  <button onClick={() => setShowNew(true)} className="text-amber-600 underline">add your first prospect</button>.
                </td></tr>
              )}
              {items.map((p) => (
                <tr key={p.id} onClick={() => setSelected(p)}
                    className="border-t border-slate-100 hover:bg-amber-50/40 cursor-pointer"
                    data-testid={`ge-row-${p.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{p.email}</div>
                  </td>
                  <td className="px-3 py-2 text-slate-700">
                    {p.company}
                    {p.job_title && <div className="text-xs text-slate-400">{p.job_title}</div>}
                  </td>
                  <td className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500">{p.region || "—"}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">
                    {p.source}
                    {p.campaign_name && (
                      <div className="text-[10px] text-amber-700 font-semibold truncate max-w-[140px]" title={p.campaign_name}>
                        📢 {p.campaign_name}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <StatusPill status={p.lead_status} meta={statusMeta[p.lead_status]}/>
                  </td>
                  <td className="px-3 py-2 text-slate-600 tabular-nums">{p.lead_score || 0}</td>
                  <td className="px-3 py-2 text-xs text-slate-400 whitespace-nowrap">
                    {p.updated_at ? new Date(p.updated_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : "—"}
                  </td>
                  <td className="px-2"><ArrowRight size={14} className="text-slate-300"/></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {loading && <div className="p-3 text-xs text-slate-400 border-t">Loading…</div>}
        <div className="px-3 py-2 text-xs text-slate-500 border-t bg-slate-50 flex justify-between">
          <span>{items.length} shown of {total}</span>
          {stats && <span>{stats.total} total across all filters</span>}
        </div>
      </div>

      {selected && <ProspectDrawer prospect={selected} statusMeta={statusMeta}
                                    onClose={() => setSelected(null)} onChanged={load}/>}
      {showNew && <NewProspectDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }}/>}
      {showCampaignMgr && <CampaignManagerDialog campaigns={campaigns} agents={agents} numbers={numbers}
                                                 onClose={() => setShowCampaignMgr(false)}
                                                 onChanged={loadCampaigns}/>}
      {showAssign && activeCampaign && <AssignLeadsDialog campaign={activeCampaign}
                                                          onClose={() => setShowAssign(false)}
                                                          onDone={() => { setShowAssign(false); loadCampaigns(); load(); }}/>}
    </div>
  );
}

// ────────────────────── CAMPAIGN CONTEXT BAR ─────────────────────────
// Shown when a specific campaign is selected in the filter — surfaces the
// assigned AI SDR, lets Admin/TL re-assign it, and batch-calls all
// queued/new leads in the campaign.
function CampaignContextBar({ campaign, leads, agents, numbers, onChanged, onAssignLeads }) {
  const [agentId, setAgentId] = useState(campaign.agent_id || "");
  const [numberId, setNumberId] = useState(campaign.voice_number_id || "");
  const [saving, setSaving] = useState(false);
  const [dialling, setDialling] = useState(false);

  useEffect(() => {
    setAgentId(campaign.agent_id || "");
    setNumberId(campaign.voice_number_id || "");
  }, [campaign.id, campaign.agent_id, campaign.voice_number_id]);

  const dirty = agentId !== (campaign.agent_id || "") || numberId !== (campaign.voice_number_id || "");

  const saveSdr = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/growth/campaigns/${campaign.id}`, {
        agent_id: agentId, voice_number_id: numberId,
      });
      toast.success("AI SDR assignment updated");
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const dialAll = async () => {
    if (!agentId || !numberId) return toast.error("Assign an AI SDR and caller ID first");
    // Only dial prospects with phone + not already meeting_booked / do_not_call.
    const skipStatuses = new Set(["meeting_booked", "meeting_scheduled", "do_not_call", "not_interested"]);
    const toCall = leads.filter((p) => (p.phone || "").trim() && !skipStatuses.has(p.lead_status));
    if (!toCall.length) return toast.error("No dialable leads in this campaign");
    if (!window.confirm(`Dial ${toCall.length} lead(s) via ${agents.find((a) => a.id === agentId)?.name || "SDR"}?`)) return;
    setDialling(true);
    let ok = 0, fail = 0;
    for (const p of toCall) {
      try {
        await api.post("/admin/growth/voice/call", {
          prospect_id: p.id, agent_id: agentId, voice_number_id: numberId,
        });
        ok++;
      } catch (e) {
        fail++;
      }
    }
    setDialling(false);
    toast.success(`Batch dial complete — ${ok} initiated, ${fail} failed`);
    onChanged();
  };

  const sdrLabel = agents.find((a) => a.id === agentId)?.name || "";

  return (
    <div className="bg-gradient-to-r from-amber-50 to-amber-100/40 border border-amber-200 rounded-lg p-4"
         data-testid="ge-campaign-context">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 flex items-center gap-1">
            <Megaphone size={11}/> Campaign
          </div>
          <div className="text-base font-bold text-slate-900 mt-0.5 truncate">{campaign.name}</div>
          {campaign.description && (
            <div className="text-xs text-slate-500 truncate max-w-xl">{campaign.description}</div>
          )}
          <div className="text-xs text-slate-600 mt-1">
            <span className="font-semibold">{campaign.lead_count || 0}</span> leads assigned
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-0.5">AI SDR</label>
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)}
                    className="text-sm px-3 py-2 border border-slate-300 rounded bg-white min-w-[220px]"
                    data-testid="ge-campaign-sdr">
              <option value="">— Select an AI SDR —</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-600 block mb-0.5">Caller ID</label>
            <select value={numberId} onChange={(e) => setNumberId(e.target.value)}
                    className="text-sm px-3 py-2 border border-slate-300 rounded bg-white min-w-[180px]"
                    data-testid="ge-campaign-number">
              <option value="">— Voice number —</option>
              {numbers.filter((n) => n.status === "active").map((n) =>
                <option key={n.id} value={n.id}>{n.country} · {n.phone_number}</option>)}
            </select>
          </div>
          {dirty && (
            <button onClick={saveSdr} disabled={saving}
                    className="text-xs px-3 py-2 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50"
                    data-testid="ge-campaign-save-sdr">
              {saving ? "Saving…" : "Save"}
            </button>
          )}
          <button onClick={onAssignLeads}
                  className="text-xs px-3 py-2 border border-slate-300 rounded bg-white hover:bg-slate-50 flex items-center gap-1"
                  data-testid="ge-campaign-assign">
            <Plus size={12}/> Assign leads
          </button>
          <button onClick={dialAll} disabled={dialling || !agentId || !numberId}
                  className="text-xs px-3 py-2 bg-amber-600 text-white rounded font-semibold hover:bg-amber-700 disabled:opacity-40 flex items-center gap-1"
                  data-testid="ge-campaign-dial-all">
            <PhoneCall size={12}/> {dialling ? "Dialling…" : "Call queued leads"}
          </button>
        </div>
      </div>
      {sdrLabel && !dirty && (
        <div className="text-[11px] text-amber-800 mt-2">
          Ready — <span className="font-semibold">{sdrLabel}</span> will call from{" "}
          <span className="font-mono">{numbers.find((n) => n.id === numberId)?.phone_number || "…"}</span>.
        </div>
      )}
    </div>
  );
}

// ────────────────────── CAMPAIGN MANAGER DIALOG ──────────────────────
function CampaignManagerDialog({ campaigns, agents, numbers, onClose, onChanged }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", agent_id: "", voice_number_id: "" });
  const [busyId, setBusyId] = useState("");

  const create = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Campaign name is required");
    setCreating(true);
    try {
      await api.post("/admin/growth/campaigns", form);
      toast.success("Campaign created");
      setForm({ name: "", description: "", agent_id: "", voice_number_id: "" });
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setCreating(false); }
  };

  const del = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Leads will be detached (not deleted).`)) return;
    setBusyId(c.id);
    try {
      await api.delete(`/admin/growth/campaigns/${c.id}`);
      toast.success("Campaign deleted");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setBusyId(""); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="ge-campaign-mgr">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-6 space-y-5 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Megaphone size={18}/> Campaigns
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
        </div>

        {/* Existing list */}
        <div className="space-y-2">
          {campaigns.length === 0 && (
            <div className="text-sm text-slate-400 text-center py-4 border border-dashed border-slate-200 rounded">
              No campaigns yet — create one below.
            </div>
          )}
          {campaigns.map((c) => {
            const agent = agents.find((a) => a.id === c.agent_id);
            const num = numbers.find((n) => n.id === c.voice_number_id);
            return (
              <div key={c.id} className="border border-slate-200 rounded p-3 flex items-start justify-between gap-3"
                   data-testid={`ge-campaign-row-${c.id}`}>
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  {c.description && <div className="text-xs text-slate-500 truncate">{c.description}</div>}
                  <div className="text-xs text-slate-500 mt-1">
                    <span className="font-semibold text-slate-700">{c.lead_count || 0}</span> leads ·
                    SDR: <span className="font-mono">{agent?.name || "—"}</span> ·
                    From: <span className="font-mono">{num?.phone_number || "—"}</span>
                  </div>
                </div>
                <button onClick={() => del(c)} disabled={busyId === c.id}
                        className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 disabled:opacity-40"
                        data-testid={`ge-campaign-del-${c.id}`}>
                  <Trash2 size={12}/> Delete
                </button>
              </div>
            );
          })}
        </div>

        {/* Create form */}
        <form onSubmit={create} className="border-t pt-4 space-y-3">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-600">New campaign</div>
          <TextInput label="Campaign name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="ge-camp-name"/>
          <TextInput label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })}/>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">AI SDR</label>
              <select value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
                      className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded" data-testid="ge-camp-agent">
                <option value="">— Choose —</option>
                {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Caller ID</label>
              <select value={form.voice_number_id} onChange={(e) => setForm({ ...form, voice_number_id: e.target.value })}
                      className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded" data-testid="ge-camp-number">
                <option value="">— Choose —</option>
                {numbers.filter((n) => n.status === "active").map((n) =>
                  <option key={n.id} value={n.id}>{n.country} · {n.phone_number}</option>)}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-slate-600 border border-slate-200 rounded">Close</button>
            <button type="submit" disabled={creating}
                    className="text-sm px-4 py-2 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50"
                    data-testid="ge-camp-create">
              {creating ? "Saving…" : "Create campaign"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ────────────────────── ASSIGN LEADS DIALOG ──────────────────────────
function AssignLeadsDialog({ campaign, onClose, onDone }) {
  const [pool, setPool] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Pull prospects NOT already in this campaign.
        const { data } = await api.get("/admin/growth/prospects", { params: { limit: 500 } });
        setPool((data.items || []).filter((p) => p.campaign_id !== campaign.id));
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
      finally { setLoading(false); }
    })();
  }, [campaign.id]);

  const filtered = useMemo(() => {
    if (!q.trim()) return pool;
    const s = q.trim().toLowerCase();
    return pool.filter((p) =>
      (p.name || "").toLowerCase().includes(s) ||
      (p.email || "").toLowerCase().includes(s) ||
      (p.company || "").toLowerCase().includes(s)
    );
  }, [pool, q]);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const submit = async () => {
    if (!selected.size) return toast.error("Pick at least one prospect");
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/growth/campaigns/${campaign.id}/assign-leads`, {
        prospect_ids: [...selected],
      });
      toast.success(`Assigned ${data.assigned} lead(s) to ${data.campaign}`);
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="ge-assign-dlg">
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl p-6 space-y-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">Assign leads → {campaign.name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, company…"
               className="w-full text-sm px-3 py-2 border border-slate-200 rounded"/>
        <div className="flex-1 overflow-y-auto border border-slate-200 rounded">
          {loading && <div className="p-6 text-center text-sm text-slate-400">Loading…</div>}
          {!loading && filtered.length === 0 && (
            <div className="p-6 text-center text-sm text-slate-400">No prospects available.</div>
          )}
          {filtered.map((p) => (
            <label key={p.id} className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 hover:bg-amber-50/40 cursor-pointer"
                   data-testid={`ge-assign-row-${p.id}`}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)}/>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-500 truncate">{p.email} · {p.company || "—"}</div>
              </div>
              {p.campaign_name && <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">in: {p.campaign_name}</span>}
            </label>
          ))}
        </div>
        <div className="flex justify-between items-center text-xs text-slate-500 pt-1">
          <span>{selected.size} selected · {filtered.length} shown</span>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <button onClick={onClose} className="text-sm px-4 py-2 text-slate-600 border border-slate-200 rounded">Cancel</button>
          <button onClick={submit} disabled={saving || !selected.size}
                  className="text-sm px-4 py-2 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50"
                  data-testid="ge-assign-submit">
            {saving ? "Assigning…" : `Assign ${selected.size} lead(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatPill({ label, value, tone = "slate", onClick }) {
  const tones = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    sky: "bg-sky-100 text-sky-800 border-sky-200",
    cyan: "bg-cyan-100 text-cyan-800 border-cyan-200",
    amber: "bg-amber-100 text-amber-800 border-amber-200",
    red: "bg-red-100 text-red-800 border-red-200",
    orange: "bg-orange-100 text-orange-800 border-orange-200",
    yellow: "bg-yellow-100 text-yellow-800 border-yellow-200",
    violet: "bg-violet-100 text-violet-800 border-violet-200",
    blue: "bg-blue-100 text-blue-800 border-blue-200",
    emerald: "bg-emerald-100 text-emerald-800 border-emerald-200",
    rose: "bg-rose-100 text-rose-800 border-rose-200",
    gray: "bg-gray-100 text-gray-800 border-gray-200",
  };
  return (
    <button onClick={onClick}
            className={"text-xs px-3 py-1.5 rounded-full border font-semibold flex items-center gap-1.5 transition " +
                       (tones[tone] || tones.slate) +
                       (onClick ? " hover:brightness-95 cursor-pointer" : " cursor-default")}>
      <span>{label}</span>
      <span className="tabular-nums font-bold">{value}</span>
    </button>
  );
}

function StatusPill({ status, meta }) {
  if (!meta) return <span className="text-xs text-slate-400">{status || "—"}</span>;
  const tones = {
    slate:"bg-slate-100 text-slate-700",  sky:"bg-sky-100 text-sky-800",
    cyan:"bg-cyan-100 text-cyan-800",     amber:"bg-amber-100 text-amber-800",
    red:"bg-red-100 text-red-800",        orange:"bg-orange-100 text-orange-800",
    yellow:"bg-yellow-100 text-yellow-800", violet:"bg-violet-100 text-violet-800",
    blue:"bg-blue-100 text-blue-800",     emerald:"bg-emerald-100 text-emerald-800",
    rose:"bg-rose-100 text-rose-800",     gray:"bg-gray-100 text-gray-800",
  };
  return (
    <span className={"inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded " + (tones[meta.color] || tones.slate)}>
      <span>{meta.emoji}</span>{meta.label}
    </span>
  );
}

// ────────────────────── DRAWER ────────────────────────────────────────
function ProspectDrawer({ prospect, statusMeta, onClose, onChanged }) {
  const [p, setP] = useState(prospect);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [showCall, setShowCall] = useState(false);

  const changeStatus = async (newStatus) => {
    if (newStatus === p.lead_status) return;
    setSaving(true);
    try {
      const { data } = await api.post(`/admin/growth/prospects/${p.id}/status`, {
        status: newStatus, note, actor: "admin"
      });
      setP(data); setNote("");
      toast.success(`Status → ${statusMeta[newStatus]?.label || newStatus}`);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm(`Delete ${p.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/growth/prospects/${p.id}`);
      toast.success("Prospect deleted");
      onChanged(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="fixed inset-0 z-40 flex" data-testid="ge-drawer">
      <div className="flex-1 bg-black/40" onClick={onClose}/>
      <div className="w-[560px] bg-white shadow-2xl overflow-y-auto flex flex-col">
        <header className="p-5 border-b flex items-start justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Prospect</div>
            <h3 className="text-lg font-bold text-slate-900">{p.name}</h3>
            <div className="text-sm text-slate-500">{p.email}</div>
            {p.company && <div className="text-sm text-slate-700 mt-1">{p.company}{p.job_title ? ` · ${p.job_title}` : ""}</div>}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl px-1">×</button>
        </header>

        <section className="p-5 border-b space-y-3">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-600">Current status</div>
          <StatusPill status={p.lead_status} meta={statusMeta[p.lead_status]}/>
          <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Optional note (e.g. 'called back, wants demo Friday')…"
                    className="w-full text-sm px-3 py-2 border border-slate-200 rounded"
                    data-testid="ge-status-note"/>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(statusMeta).map(([k, m]) => (
              <button key={k} onClick={() => changeStatus(k)} disabled={saving || k === p.lead_status}
                      className={"text-[11px] px-2.5 py-1 rounded border transition " +
                                 (k === p.lead_status
                                   ? "border-amber-500 bg-amber-100 text-amber-900 font-bold cursor-default"
                                   : "border-slate-200 hover:border-amber-400 hover:bg-amber-50")}
                      data-testid={`ge-status-btn-${k}`}>
                {m.emoji} {m.label}
              </button>
            ))}
          </div>
        </section>

        <section className="p-5 border-b">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-2">Attributes</div>
          <dl className="text-sm space-y-1">
            <FieldRow label="Region"   value={(p.region || "—").toUpperCase()}/>
            <FieldRow label="Country"  value={p.country || "—"}/>
            <FieldRow label="Phone"    value={p.phone || "—"}/>
            <FieldRow label="Source"   value={p.source + (p.source_service_slug ? ` · ${p.source_service_slug}` : "")}/>
            <FieldRow label="Score"    value={p.lead_score || 0}/>
            <FieldRow label="Created"  value={p.created_at ? new Date(p.created_at).toLocaleString() : "—"}/>
          </dl>
          {p.notes && (
            <div className="mt-3 text-sm text-slate-700 bg-slate-50 p-3 rounded border border-slate-100 whitespace-pre-wrap">
              {p.notes}
            </div>
          )}
        </section>

        <section className="p-5 border-b flex-1">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-600 mb-2">Timeline</div>
          <ol className="relative border-l border-slate-200 pl-4 space-y-3">
            {[...(p.status_history || [])].reverse().map((h, i) => {
              const m = statusMeta[h.status] || {};
              return (
                <li key={i} className="text-sm">
                  <div className="absolute -left-1.5 mt-1 w-3 h-3 rounded-full bg-amber-400 border-2 border-white"/>
                  <div className="flex items-center gap-2 flex-wrap">
                    <StatusPill status={h.status} meta={m}/>
                    <span className="text-xs text-slate-400">{h.at ? new Date(h.at).toLocaleString() : ""}</span>
                  </div>
                  {h.actor && <div className="text-[11px] text-slate-500 mt-0.5">by <span className="font-mono">{h.actor}</span></div>}
                  {h.note && <div className="text-xs text-slate-600 mt-0.5">{h.note}</div>}
                </li>
              );
            })}
          </ol>
        </section>

        <footer className="p-4 border-t flex justify-between items-center">
          <button onClick={del} className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1" data-testid="ge-drawer-delete">
            <Trash2 size={12}/> Delete prospect
          </button>
          <div className="flex gap-2">
            <button onClick={() => setShowCall(true)} disabled={!p.phone}
                    title={p.phone ? "Trigger an AI voice call to this prospect" : "Add a phone number to enable calling"}
                    className="text-xs px-3 py-1.5 border border-amber-300 bg-amber-50 text-amber-900 rounded hover:bg-amber-100 disabled:opacity-40 flex items-center gap-1 font-semibold"
                    data-testid="ge-drawer-call">
              <PhoneCall size={12}/> Call now
            </button>
            <button onClick={onClose} className="text-xs text-slate-600 px-4 py-1.5 border border-slate-200 rounded">Close</button>
          </div>
        </footer>
      </div>
      {showCall && <CallProspectDialog prospect={p} onClose={() => setShowCall(false)}
                                       onCalled={() => { setShowCall(false); onChanged(); toast.success("Call initiated"); }}/>}
    </div>
  );
}

function CallProspectDialog({ prospect, onClose, onCalled }) {
  const [agents, setAgents] = useState([]);
  const [numbers, setNumbers] = useState([]);
  const [pick, setPick] = useState({ agent_id: "", voice_number_id: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [a, n] = await Promise.all([
          api.get("/admin/growth/voice/agents"),
          api.get("/admin/growth/voice/numbers"),
        ]);
        setAgents(a.data.items || []);
        setNumbers(n.data.items || []);
        // Auto-pick first agent + first matching-region number.
        if (a.data.items?.[0]) setPick((p) => ({ ...p, agent_id: a.data.items[0].id }));
        const matched = (n.data.items || []).find((x) => x.status === "active");
        if (matched) setPick((p) => ({ ...p, voice_number_id: matched.id }));
      } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!pick.agent_id || !pick.voice_number_id) return toast.error("Pick an agent and a voice number");
    setSaving(true);
    try {
      await api.post("/admin/growth/voice/call", {
        prospect_id: prospect.id, ...pick,
      });
      onCalled();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="call-dialog">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold flex items-center gap-2"><PhoneCall size={18}/> Call {prospect.name}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
        </div>
        <div className="text-xs text-slate-500 bg-slate-50 p-3 rounded border border-slate-200">
          Dialling <span className="font-mono text-slate-900">{prospect.phone}</span> via ElevenLabs + Twilio.
          The AI agent will conduct a live conversation, then update this prospect&apos;s status based on the call outcome.
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Agent</label>
          <select value={pick.agent_id} onChange={(e) => setPick({ ...pick, agent_id: e.target.value })}
                  className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded">
            <option value="">— Choose an agent —</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.region}/{a.language})</option>)}
          </select>
          {agents.length === 0 && <div className="text-[11px] text-red-600 mt-1">No agents yet — create one in AI Calling tab.</div>}
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Caller ID (voice number)</label>
          <select value={pick.voice_number_id} onChange={(e) => setPick({ ...pick, voice_number_id: e.target.value })}
                  className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded">
            <option value="">— Choose a number —</option>
            {numbers.filter((n) => n.status === "active").map((n) =>
              <option key={n.id} value={n.id}>{n.country} · {n.phone_number}</option>)}
          </select>
          {numbers.filter((n) => n.status === "active").length === 0 &&
            <div className="text-[11px] text-red-600 mt-1">No active number — buy one in AI Calling tab first.</div>}
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-slate-600 border border-slate-200 rounded">Cancel</button>
          <button type="submit" disabled={saving || !pick.agent_id || !pick.voice_number_id}
                  className="text-sm px-4 py-2 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center gap-1"
                  data-testid="call-submit">
            <PhoneCall size={12}/> {saving ? "Dialling…" : "Call now"}
          </button>
        </div>
      </form>
    </div>
  );
}
function FieldRow({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-900 text-right">{value}</dd>
    </div>
  );
}

// ────────────────────── NEW PROSPECT DIALOG ──────────────────────────
function NewProspectDialog({ onClose, onCreated }) {
  const [f, setF] = useState({ name: "", email: "", company: "", phone: "", job_title: "", country: "", source: "manual" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim()) return toast.error("Name and email are required");
    setSaving(true);
    try {
      await api.post("/admin/growth/prospects", f);
      toast.success("Prospect created");
      onCreated();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40" data-testid="ge-new-dialog">
      <form onSubmit={submit} className="bg-white rounded-lg shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold">Add prospect</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-xl">×</button>
        </div>
        <TextInput label="Full name *"  value={f.name}      onChange={(v) => setF({ ...f, name: v })} testid="ge-new-name"/>
        <TextInput label="Business email *" value={f.email}  onChange={(v) => setF({ ...f, email: v })} type="email" testid="ge-new-email"/>
        <div className="grid grid-cols-2 gap-3">
          <TextInput label="Company"    value={f.company}   onChange={(v) => setF({ ...f, company: v })}/>
          <TextInput label="Job title"  value={f.job_title} onChange={(v) => setF({ ...f, job_title: v })}/>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <TextInput label="Phone"      value={f.phone}     onChange={(v) => setF({ ...f, phone: v })}/>
          <TextInput label="Country"    value={f.country}   onChange={(v) => setF({ ...f, country: v })} placeholder="US, GB, IN, DE…"/>
        </div>
        <div>
          <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">Source</label>
          <select value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })}
                  className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded">
            <option value="manual">Manual</option>
            <option value="referral">Referral</option>
            <option value="linkedin">LinkedIn</option>
            <option value="other">Other</option>
          </select>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 text-slate-600 border border-slate-200 rounded">Cancel</button>
          <button type="submit" disabled={saving}
                  className="text-sm px-4 py-2 bg-amber-500 text-white rounded font-semibold hover:bg-amber-600 disabled:opacity-50"
                  data-testid="ge-new-save">
            {saving ? "Saving…" : "Create prospect"}
          </button>
        </div>
      </form>
    </div>
  );
}
function TextInput({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <div>
      <label className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
             data-testid={testid}
             className="w-full text-sm px-3 py-2 mt-1 border border-slate-200 rounded focus:outline-none focus:border-amber-500"/>
    </div>
  );
}

// ────────────────────── PHASE PLACEHOLDER ────────────────────────────
function PhasePlaceholder({ title, phase }) {
  return (
    <div className="bg-white border border-slate-200 border-dashed rounded-lg p-10 text-center" data-testid="ge-placeholder">
      <div className="text-[10px] font-bold uppercase tracking-widest text-amber-600">Coming — {phase}</div>
      <h3 className="text-lg font-bold text-slate-900 mt-2">{title}</h3>
      <p className="text-sm text-slate-500 mt-2 max-w-md mx-auto">
        Foundation (Phase 1) is live. Waiting on your ElevenLabs Agent ID + Twilio voice number
        to build out the calling engine and n8n email workflows.
      </p>
    </div>
  );
}
